<template>
  <dialog
    ref="dialog"
    :class="['modal-dialog', sizeClass]"
    tabindex="-1"
    @cancel="onCancel"
    @click="onBackdropClick"
  >
    <div class="modal-content">
      <header class="modal-header">
        <h5
          v-if="$slots.title"
          class="modal-title"
        >
          <slot name="title"></slot>
        </h5>
        <button
          v-if="dismissible"
          class="close"
          type="button"
          @click="onCloseDialog"
        >
          ✖
        </button>
      </header>

      <div
        class="modal-body"
        :style="bodyStyle"
      >
        <slot></slot>
      </div>

      <footer
        v-if="$slots.buttons"
        class="modal-footer"
      >
        <slot name="buttons"></slot>
      </footer>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

const dialog = ref<HTMLDialogElement>();
const emit = defineEmits(["close"]);
const closeDialog = () => {
  emit("close");
  dialog.value!.close();
};
const openDialog = () => {
  dialog.value!.showModal();
  dialog.value!.focus({ preventScroll: true });
};
defineExpose({ closeDialog, openDialog });

interface Props {
  close?: () => void;
  size?: "sm" | "md" | "lg" | "xl";
  bodyStyle?: string | Record<string, string>;
  closeOnBackdropClick?: boolean;
  dismissible?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  size: "lg",
  bodyStyle: undefined,
  closeOnBackdropClick: false,
  dismissible: true,
});

const sizeClass = computed(() => {
  return props.size ? `modal-${props.size}` : "";
});
const onCancel = (event: Event) => {
  if (!props.dismissible) {
    event.preventDefault();
    return;
  }
  emit("close");
};
const onCloseDialog = () => {
  if (props.close) {
    props.close();
  } else {
    closeDialog();
  }
};
const onBackdropClick = (event: MouseEvent) => {
  if (!props.closeOnBackdropClick) return;
  const modalContent = dialog.value?.querySelector(".modal-content");
  if (!modalContent) return;
  const rect = modalContent.getBoundingClientRect();
  const isInModal =
    rect.top <= event.clientY &&
    event.clientY <= rect.bottom &&
    rect.left <= event.clientX &&
    event.clientX <= rect.right;
  if (!isInModal) {
    onCloseDialog();
  }
};
</script>

<style scoped>
dialog {
  border: none;
  border-radius: 5px;
  padding: 0;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  overflow: visible;
  pointer-events: auto !important;
  width: calc(100vw - 2rem);
}

.modal-content {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-body {
  overflow-y: auto;
}

dialog::backdrop {
  background-color: rgba(27, 27, 31, 0.6);
}
</style>
